import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import ErrorPanel from './ErrorPanel'

/**
 * Crash containment (register S27).
 *
 * React unmounts the whole tree when a render throws and nothing catches it.
 * With no boundary anywhere in the app, one page reading a field a live loan
 * does not carry (Portfolio Analytics, `d.triggers.some`) blanked the screen —
 * navigation included — until a manual reload. Two boundaries now exist:
 *
 * - `RouteErrorBoundary` sits around the routed page inside Layout, so the
 *   sidebar and header survive and the user can navigate away. It resets when
 *   the route changes (`resetKey`), so leaving the broken page recovers.
 * - `AppErrorBoundary` sits at the root as the last line: if Layout itself
 *   throws, the user still gets a message and a reload button, never a void.
 *
 * Both report through Sentry (a no-op when no DSN is configured) so a crash
 * is recorded rather than only seen.
 */

type Scope = 'route' | 'app'

interface BoundaryProps {
  children: ReactNode
  /** When this value changes, a caught error is cleared and children re-render. */
  resetKey?: string
}

interface BoundaryState {
  error: Error | null
}

function report(scope: Scope, error: Error, info: ErrorInfo) {
  Sentry.captureException(error, {
    tags: { boundary: scope },
    extra: { componentStack: info.componentStack ?? '' },
  })
}

export class RouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    report('route', error, info)
  }

  componentDidUpdate(prev: BoundaryProps) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  private retry = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <ErrorPanel
        title="This screen hit an error and could not be shown"
        detail={`${error.message || 'Unknown error'}. The rest of the platform is unaffected — use the navigation to continue, or try this screen again. The error has been recorded.`}
        actionLabel="Try this screen again"
        onAction={this.retry}
      />
    )
  }
}

export class AppErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    report('app', error, info)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <div className="flex h-screen w-full bg-[#090909]">
        <ErrorPanel
          title="CapitalMarketsOS hit an error"
          detail={`${error.message || 'Unknown error'}. Reload to continue. The error has been recorded.`}
          actionLabel="Reload"
          onAction={() => window.location.reload()}
        />
      </div>
    )
  }
}
